import { useRef } from 'react'

import { DeviceI, InsetsI, RectI, Size } from '../types/types'
import { getDeviceClientRect, isPointWithinBounds } from '../utils/chart'

const REBRAND_TRASH_HEIGHT = 72

export const useFloorPosition = (
  chartSize: Size,
  chartInsets: InsetsI,
  mapImageSize: Size
) => {
  const createBinPosition = (): RectI => {
    const [width] = chartSize
    const binSize = [150, 115]
    const areaPadding = 40
    return {
      x: width - binSize[0] - areaPadding,
      y: areaPadding,
      width: binSize[0],
      height: binSize[1],
    }
  }

  const binPosition = createBinPosition()
  const rebrandBinPosition = {
    ...binPosition,
    height: REBRAND_TRASH_HEIGHT,
    y: binPosition.y + (binPosition.height - REBRAND_TRASH_HEIGHT) / 2,
  }
  const binPositionRef = useRef(binPosition)
  const rebrandBinPositionRef = useRef(rebrandBinPosition)
  // We create a ref of binPosition, because in the validateTrashPosition function the 'binPosition' is 'stale'.
  // (I.e the state before the devicePlacementDrawer is toggled. useRef uses the up to date value.)
  binPositionRef.current = binPosition
  rebrandBinPositionRef.current = rebrandBinPosition

  const validateMapPosition = (x: number, y: number): boolean => {
    const [imageWidth, imageHeight] = mapImageSize
    return isPointWithinBounds(x, y, 0, 0, imageWidth, imageHeight)
  }

  const validateTrashPosition = (x: number, y: number): boolean => {
    return isPointWithinBounds(
      x,
      y,
      binPositionRef.current.x,
      binPositionRef.current.y,
      binPositionRef.current.x + binPositionRef.current.width,
      binPositionRef.current.y + binPositionRef.current.height
    )
  }

  const validateRebrandTrashPosition = (x: number, y: number): boolean => {
    return isPointWithinBounds(
      x,
      y,
      rebrandBinPositionRef.current.x,
      rebrandBinPositionRef.current.y,
      rebrandBinPositionRef.current.x + rebrandBinPositionRef.current.width,
      rebrandBinPositionRef.current.y + rebrandBinPositionRef.current.height
    )
  }

  const isDeviceInTrash = (deviceData: DeviceI): boolean => {
    const deviceRect = getDeviceClientRect(deviceData.id)

    const { x: clientX, y: clientY, width, height } = deviceRect
    // `chartInsets` may need to be in a ref if its left/top values become
    // dynamic. Today only `.right` changes with the drawer and is irrelevant
    // to this screen-coordinate conversion.
    return validateRebrandTrashPosition(
      clientX + width * 0.5 - chartInsets.left,
      clientY + height * 0.5 - chartInsets.top
    )
  }

  const validateRebrandDevicePosition = (deviceData: DeviceI): boolean => {
    return (
      validateMapPosition(deviceData.x, deviceData.y) &&
      !isDeviceInTrash(deviceData)
    )
  }

  const validateDevicePosition = (deviceData: DeviceI): boolean => {
    const { x, y, id } = deviceData
    const isOnMap = validateMapPosition(x, y)

    const deviceRect = getDeviceClientRect(id)
    const { x: clientX, y: clientY, width, height } = deviceRect

    // `chartInsets` may need to be in a ref,
    // `.right` might change in the future and be stale in this callback
    // but we don't use this property here
    const isInTrash = validateTrashPosition(
      clientX + width * 0.5 - chartInsets.left,
      clientY + height * 0.5 - chartInsets.top
    )
    return isOnMap && !isInTrash
  }

  return {
    binPosition: binPosition,
    isDeviceInTrash,
    rebrandBinPosition,
    validateDevicePosition,
    validateMapPosition,
    validateRebrandDevicePosition,
  }
}
